
import { Transaction, FIFOPaymentResult } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================
// HELPER: Detectar se é despesa de cartão de crédito
// Usa cardId como fonte da verdade (não depende de paymentMethod)
// ============================================================
export const isCardExpense = (transaction: Transaction): boolean => {
  // Pagamentos de fatura NÃO são despesas de cartão
  if (transaction.isBillPayment) return false;
  
  return Boolean(transaction.cardId && transaction.type === 'despesa');
};

// Função para inicializar campos obrigatórios em transações de cartão
export const initializeTransactionFields = (transaction: Transaction): Transaction => {
  // CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
  if (!isCardExpense(transaction)) {
    return transaction;
  }

  // Usar originalAmount se existir, senão usar Math.abs(amount)
  // transaction.amount já vem descriptografado do contexto
  const effectiveAmount = transaction.originalAmount || Math.abs(transaction.amount);
  
  // Se effectiveAmount ainda for 0, logar warning para debug
  if (effectiveAmount <= 0) {
    logger.warn(`[FIFO] Transaction ${transaction.id} has zero/invalid amount`);
  }

  return {
    ...transaction,
    originalAmount: effectiveAmount,
    paidPortion: transaction.paidPortion || 0,
    status: transaction.status || 'pendente'
  };
};

// Interface para resultado de validação
export interface FIFOValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// Função para validar dados antes da aplicação FIFO
// Retorna objeto com detalhes em vez de apenas boolean
export const validateTransactionsForFIFO = (transactions: Transaction[]): FIFOValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  for (const transaction of transactions) {
    // CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
    if (isCardExpense(transaction)) {
      if (!transaction.originalAmount || transaction.originalAmount <= 0) {
        // É um warning, não erro - a inicialização deve ter corrigido
        warnings.push(`Transaction ${transaction.id}: originalAmount inválido`);
      }
      if (transaction.paidPortion === undefined || transaction.paidPortion < 0) {
        warnings.push(`Transaction ${transaction.id}: paidPortion inválido`);
      }
    }
  }
  
  if (errors.length > 0) {
    console.error('[FIFO Validation] Errors:', errors);
  }
  if (warnings.length > 0) {
    logger.debug('[FIFO Validation] Warnings', { count: warnings.length });
  }
  
  return { valid: errors.length === 0, errors, warnings };
};

// Aplicar pagamento FIFO com validação melhorada
export const applyFIFOPayment = (
  transactions: Transaction[],
  paymentAmount: number
): FIFOPaymentResult => {
  logger.debug('[FIFO] Starting payment', { paymentAmount, transactionCount: transactions.length });

  // Inicializar e validar transações
  const initializedTransactions = transactions.map(initializeTransactionFields);
  
  const validation = validateTransactionsForFIFO(initializedTransactions);
  if (!validation.valid) {
    console.error('[FIFO] Validation failed:', validation.errors);
    throw new Error(`Dados de transações inválidos para aplicação FIFO: ${validation.errors.join(', ')}`);
  }

  // Ordenar por data (mais antigas primeiro - FIFO)
  const sortedTransactions = [...initializedTransactions].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const appliedTransactions: FIFOPaymentResult['appliedTransactions'] = [];
  const updatedTransactions: Transaction[] = [];
  let remainingPayment = paymentAmount;

  for (const transaction of sortedTransactions) {
    if (remainingPayment <= 0) {
      updatedTransactions.push(transaction);
      continue;
    }

    const originalAmount = transaction.originalAmount!;
    const currentPaidPortion = transaction.paidPortion!;
    const remainingDebt = originalAmount - currentPaidPortion;

    if (remainingDebt <= 0) {
      // Transação já totalmente paga
      updatedTransactions.push({
        ...transaction,
        status: 'efetivada'
      });
      continue;
    }

    const appliedAmount = Math.min(remainingPayment, remainingDebt);
    const newPaidPortion = currentPaidPortion + appliedAmount;
    const isFullyPaid = newPaidPortion >= originalAmount;

    // Atualizar transação - MANTER VALOR ORIGINAL
    const updatedTransaction: Transaction = {
      ...transaction,
      originalAmount: originalAmount, // Garantir que o valor original seja preservado
      paidPortion: newPaidPortion,
      status: isFullyPaid ? 'efetivada' : 'pendente'
    };

    updatedTransactions.push(updatedTransaction);

    // Registrar pagamento aplicado
    appliedTransactions.push({
      id: transaction.id,
      description: transaction.description,
      originalAmount,
      appliedAmount,
      remainingAmount: originalAmount - newPaidPortion,
      isFullyPaid,
      paidPortion: newPaidPortion
    });

    remainingPayment -= appliedAmount;
  }

  const result: FIFOPaymentResult = {
    appliedTransactions,
    totalApplied: paymentAmount - remainingPayment,
    remainingPayment,
    updatedTransactions: updatedTransactions as import('@/types/core').Transaction[]
  };

  logger.debug('[FIFO] Payment complete', {
    totalApplied: result.totalApplied,
    transactionsAffected: result.appliedTransactions.length,
    remainingPayment: result.remainingPayment
  });

  return result;
};

// Helper para normalizar status (aceita valores em português ou inglês)
const isPaidStatus = (status?: string): boolean => {
  return status === 'paid' || status === 'efetivada';
};

const isPartialStatus = (status?: string): boolean => {
  return status === 'partial';
};

// Calcular status mais preciso de transação
// CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
export const calculateTransactionStatus = (transaction: Transaction): 'paid' | 'partial' | 'pending' => {
  if (!isCardExpense(transaction)) {
    return isPaidStatus(transaction.status) ? 'paid' : 'pending';
  }

  // Para parcelamentos importados via OFX, respeitar o status salvo explicitamente
  // Pois parcelas passadas são criadas com status 'paid' durante a importação
  if (transaction.installmentNumber && transaction.installments && transaction.installments > 1) {
    if (isPaidStatus(transaction.status)) return 'paid';
    if (isPartialStatus(transaction.status)) return 'partial';
    return 'pending';
  }

  // Para outras transações de cartão, calcular baseado em paidPortion (pagamento de fatura)
  const paidPortion = transaction.paidPortion || 0;
  const originalAmount = transaction.originalAmount || Math.abs(transaction.amount);

  if (paidPortion <= 0) return 'pending';
  if (paidPortion >= originalAmount) return 'paid';
  return 'partial';
};

// Obter valor efetivo para relatórios
// CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
export const getEffectiveAmount = (transaction: Transaction): number => {
  if (!isCardExpense(transaction)) {
    return transaction.amount;
  }

  const originalAmount = transaction.originalAmount || Math.abs(transaction.amount);
  const paidPortion = transaction.paidPortion || 0;
  const remainingAmount = Math.max(0, originalAmount - paidPortion);
  
  return transaction.type === 'despesa' ? -remainingAmount : remainingAmount;
};

/**
 * Calcula o valor efetivo de uma transação para relatórios de CATEGORIA
 * CORRIGIDO: Categorias mostram o valor ORIGINAL da compra, independente de pago ou não
 * 
 * Lógica: O gráfico de categorias representa QUANTO FOI GASTO em cada categoria,
 * não quanto ainda falta pagar. Então usamos sempre o valor original.
 */
export const getEffectiveAmountForCategory = (transaction: Transaction): number => {
  // Usar originalAmount se existir (para cartões com FIFO), senão usar amount
  return transaction.originalAmount || Math.abs(transaction.amount);
};

/**
 * Calcula valores separados (pago vs pendente) para CashFlow e StatCards
 * Considera paidPortion para transações de cartão parcialmente pagas
 * 
 * CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
 * 
 * Retorna:
 * - paidAmount: quanto já foi efetivamente pago (afeta saldo atual)
 * - pendingAmount: quanto ainda resta pagar (afeta saldo projetado)
 */
export const getEffectiveAmountsForCashFlow = (transaction: Transaction): { 
  paidAmount: number; 
  pendingAmount: number; 
} => {
  // CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
  if (!isCardExpense(transaction)) {
    const isPaid = isPaidStatus(transaction.status) || transaction.status === undefined;
    return {
      paidAmount: isPaid ? Math.abs(transaction.amount) : 0,
      pendingAmount: isPaid ? 0 : Math.abs(transaction.amount)
    };
  }
  
  const originalAmount = transaction.originalAmount || Math.abs(transaction.amount);
  const paidPortion = transaction.paidPortion || 0;
  const status = calculateTransactionStatus(transaction);
  
  if (status === 'paid') {
    return { paidAmount: originalAmount, pendingAmount: 0 };
  }
  
  if (status === 'partial') {
    return { 
      paidAmount: paidPortion, 
      pendingAmount: Math.max(0, originalAmount - paidPortion)
    };
  }
  
  // pending
  return { paidAmount: 0, pendingAmount: originalAmount };
};

// Filtrar transações para relatórios (excluir pagas, mostrar valores efetivos)
// IMPORTANT: Excludes bill payment transactions to avoid double counting
export const getEffectiveTransactions = (transactions: Transaction[]): Transaction[] => {
  const migratedTransactions = migrateTransactionData(transactions);
  
  return migratedTransactions
    .map(transaction => {
      // Filtrar transações deletadas e pagamentos de fatura
      if (transaction.deleted) return null;
      if (transaction.isBillPayment) return null;
      
      // CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
      if (!isCardExpense(transaction)) {
        return transaction;
      }
      
      const status = calculateTransactionStatus(transaction);
      
      // Filtrar transações completamente pagas (não devem aparecer em futuros/agenda)
      if (status === 'paid') {
        return null;
      }
      
      // Para transações parciais, ajustar o valor para mostrar apenas o restante
      if (status === 'partial') {
        return {
          ...transaction,
          amount: getEffectiveAmount(transaction)
        };
      }
      
      // Para transações pendentes, incluir normalmente
      return transaction;
    })
    .filter((transaction): transaction is Transaction => transaction !== null);
};

// NEW: Get all transactions for dashboard calculations (including paid ones)
// OPÇÃO B: Inclui pagamentos de fatura, exclui despesas de cartão já pagas
export const getAllTransactionsForDashboard = (transactions: Transaction[]): Transaction[] => {
  const migratedTransactions = migrateTransactionData(transactions);
  
  return migratedTransactions
    .filter(transaction => {
      // Filtrar transações deletadas
      if (transaction.deleted) return false;
      
      // Pagamentos de fatura - INCLUIR (representa a saída real de caixa)
      if (transaction.isBillPayment) return true;
      
      // CORRIGIDO: Usar calculateTransactionStatus em vez de status === 'paid'
      if (isCardExpense(transaction) && calculateTransactionStatus(transaction) === 'paid') {
        return false;
      }
      
      return true;
    })
    .map(transaction => {
      // CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
      if (!isCardExpense(transaction)) {
        return transaction;
      }
      
      const status = calculateTransactionStatus(transaction);
      
      // Para transações completamente pagas, incluir com valor original
      if (status === 'paid') {
        return {
          ...transaction,
          amount: transaction.originalAmount || Math.abs(transaction.amount)
        };
      }
      
      // Para transações parciais, incluir com valor original
      if (status === 'partial') {
        return {
          ...transaction,
          amount: transaction.originalAmount || Math.abs(transaction.amount)
        };
      }
      
      // Para transações pendentes, incluir normalmente
      return transaction;
    })
    .filter((transaction): transaction is Transaction => transaction !== null);
};

// Gerar resumo detalhado do pagamento
export const generatePaymentSummary = (fifoResult: FIFOPaymentResult) => {
  const fullyPaidTransactions = fifoResult.appliedTransactions.filter(t => t.isFullyPaid);
  const partiallyPaidTransactions = fifoResult.appliedTransactions.filter(t => !t.isFullyPaid);

  return {
    fullyPaidTransactions,
    partiallyPaidTransactions,
    totalTransactionsAffected: fifoResult.appliedTransactions.length,
    fullyPaidCount: fullyPaidTransactions.length,
    partiallyPaidCount: partiallyPaidTransactions.length,
    totalApplied: fifoResult.totalApplied,
    remainingPayment: fifoResult.remainingPayment,
    summary: {
      totalPaid: fullyPaidTransactions.reduce((sum, t) => sum + t.originalAmount, 0),
      totalPartial: partiallyPaidTransactions.reduce((sum, t) => sum + t.appliedAmount, 0),
      limitReleased: fifoResult.totalApplied
    }
  };
};

// Migrar dados existentes para garantir consistência
// CORRIGIDO: Usar isCardExpense (cardId) em vez de paymentMethod
export const migrateTransactionData = (transactions: Transaction[]): Transaction[] => {
  return transactions.map(transaction => {
    if (isCardExpense(transaction)) {
      return {
        ...transaction,
        originalAmount: transaction.originalAmount || Math.abs(transaction.amount),
        paidPortion: transaction.paidPortion || 0,
        status: transaction.status || 'pendente'
      };
    }
    return transaction;
  });
};
